// Headless process (BPMN / swimlane) render helpers — the shared home for the
// walk→scaffold→layout→SVG recipe used by the MCP `arch_render` tool, the
// preview server's /api/process endpoints, and the `archlang render` CLI.
//
// Replicates the UI's FRESH-OPEN walk so a headless export looks like what a
// user sees when they open the process view, then projects the resulting
// `BpmnLayout` / `SwimlaneLayout` through `@archlang/scene/process` into a
// portable SVG.
//
//   • fresh-open BPMN walk — default branch selection, the ALL-BRANCHES walk
//     (conditional alternatives stay visible as gateway bands), subprocesses
//     collapsed. Mirrors useProcessLayouts.ts:53-55 (`bpmnLayout(buildScaffold(
//     flowSource, ctx))`, flowSource = the `{ showAllBranches: true }` walk) and
//     ProcessDiffView.tsx:49-54 (initial WalkerState
//     `{ branchSelection: new Map(), expandedSubprocesses: new Set() }`).
//   • diff — the exact pure recipe ProcessDiffView uses (walk ×2 →
//     buildScaffold ×2 → diffScaffolds → bpmnLayout(union)),
//     ProcessDiffView.tsx:46-102.

import {
  bpmnLayout,
  buildScaffold,
  diffScaffolds,
  moduleNameIndex,
  swimlaneContextFor,
  swimlaneLayout,
  walkProcess,
  walkerContextFor,
  perStepLatency,
  buildLatencyEnv,
  type DiffKeyContext,
  type ProcessModel,
  type ProcessScaffold,
  type ResolvedModel,
  type StableId,
  type SwimlaneVariant,
  type WalkerState,
} from "@archlang/engine";
import { renderProcessSvg, renderSwimlaneSvg, type BpmnOwner } from "@archlang/scene/process";
import { aspectColorFor } from "./aspect-color.js";
import { resolveExpandedSubprocesses, swimlaneExportOptions } from "./process-swimlane-export.js";

/**
 * Owner tint for every BPMN render — the ONE place the rule lives.
 *
 * `override` is the app's "colour by" picker, keyed by owner path; without it
 * the palette's value hash answers. The in-app download passes one, headless
 * exports do not, and neither restates the actor/hash decision — an earlier
 * copy of it in app-shell drifted and reintroduced a second palette.
 *
 * Scene is dependency-free and cannot reach the palette's `swatchFor`, so
 * `compose-bpmn` paints an owner nobody coloured as chrome. The `ownerColor`
 * hook exists so a layer that CAN reach the hash supplies the real tint, and
 * viewer-core is that layer: it depends on engine and scene both. Passing it is
 * what keeps one owner label reading as one colour across the board, the aspect
 * overlay, a `colorize(@@team)` view and a BPMN export. Actors defer — scene
 * owns that tint.
 */
export function bpmnOwnerColor(
  override?: (path: readonly string[]) => string | undefined,
): (o: BpmnOwner) => string | undefined {
  return (o) => (o.isActor ? undefined : (override?.(o.path) ?? aspectColorFor(o.label)));
}

/** The rule with no app-side override — every headless export. */
export const BPMN_EXPORT_OPTIONS = { ownerColor: bpmnOwnerColor() } as const;

/** Qualified dotted name of a process: `<ownerPath…>.<name>` (bare `name` when
 *  free-standing). The resolution rule shared with the server endpoint. */
export function processQualifiedName(p: ProcessModel): string {
  const owner = p.ownerPath ?? [];
  return owner.length > 0 ? [...owner, p.name].join(".") : p.name;
}

/** Resolve a process by its qualified dotted name against `model.processModels`.
 *  Anonymous processes (no name) are unaddressable. */
export function resolveProcessByName(
  model: ResolvedModel,
  qualified: string,
): ProcessModel | null {
  const procs = model.processModels ?? [];
  return (
    procs.find((p) => !p.anonymous && p.name && processQualifiedName(p) === qualified) ?? null
  );
}

/** Match a process across workspaces: StableId first, qualified-name fallback
 *  (a rename keeps the two sides paired iff both carry the same id). */
export function matchProcess(model: ResolvedModel, target: ProcessModel): ProcessModel | null {
  const procs = model.processModels ?? [];
  if (target.id) {
    const byId = procs.find((p) => p.id === target.id);
    if (byId) return byId;
  }
  const name = processQualifiedName(target);
  return (
    procs.find((p) => !p.anonymous && p.name && processQualifiedName(p) === name) ?? null
  );
}

const freshState = (expanded: ReadonlySet<string>): WalkerState => ({
  branchSelection: new Map(),
  expandedSubprocesses: expanded,
});

export function freshBpmnScaffold(
  model: ResolvedModel,
  proc: ProcessModel,
  expand?: "all" | readonly string[],
): ProcessScaffold {
  const ctx = walkerContextFor(model, proc);
  const swimlaneCtx = swimlaneContextFor(model);
  const expanded = resolveExpandedSubprocesses(proc, ctx, expand);
  const walk = walkProcess(proc, freshState(expanded), ctx, { showAllBranches: true });
  return buildScaffold(walk, swimlaneCtx);
}

/** Fresh-open flow/sequence swimlane → portable SVG for one process. Same
 *  all-branches walk the UI's `useProcessLayouts` feeds both variants; the
 *  walk-derived composer options come from the shared viewer-core builder. */
export function renderProcessSwimlaneSvg(
  model: ResolvedModel,
  proc: ProcessModel,
  variant: SwimlaneVariant,
  expand?: "all" | readonly string[],
): string {
  const ctx = walkerContextFor(model, proc);
  const swimlaneCtx = swimlaneContextFor(model);
  const expanded = resolveExpandedSubprocesses(proc, ctx, expand);
  const walk = walkProcess(proc, freshState(expanded), ctx, { showAllBranches: true });
  const layout = swimlaneLayout(walk, swimlaneCtx, { variant });
  const stepLat = new Map(perStepLatency(walk, buildLatencyEnv(model, proc)).map((r) => [r.stepIndex, r]));
  return renderSwimlaneSvg(
    layout,
    swimlaneExportOptions(walk, layout, (i) => stepLat.get(i)?.label, (i) => stepLat.get(i)?.cumLabel),
  );
}

/** Fresh-open BPMN → portable SVG for one process. */
export function renderProcessBpmnSvg(
  model: ResolvedModel,
  proc: ProcessModel,
  expand?: "all" | readonly string[],
): string {
  const layout = bpmnLayout(freshBpmnScaffold(model, proc, expand));
  return renderProcessSvg(layout, BPMN_EXPORT_OPTIONS);
}

// An empty scaffold — a fully added/removed process diffs against this (the
// matcher tags every node, geometry still comes from ONE layout).
// Mirrors ProcessDiffView.tsx:34-44.
const EMPTY_SCAFFOLD: ProcessScaffold = {
  tree: [],
  linear: [],
  byIndex: new Map(),
  collapsedGroups: new Map(),
  parallelGroups: new Map(),
  reentries: [],
  compensation: [],
  triggers: [],
  diagnostics: [],
};

/** Name → stable id for `do X` matching through a rename of X.
 *  Mirrors ProcessDiffView.tsx:58-63. */
function callableIndex(model: ResolvedModel): Map<string, StableId> {
  const out = new Map<string, StableId>();
  for (const s of model.subprocessModels ?? []) if (s.id) out.set(s.name, s.id);
  for (const p of model.processModels ?? []) if (p.id && p.name && !p.anonymous) out.set(p.name, p.id);
  return out;
}

/**
 * Diff-tagged BPMN → portable SVG. Both sides are walked + scaffolded with the
 * fresh-open recipe, matched by `diffScaffolds`, and laid out ONCE through
 * `bpmnLayout(union)` (ProcessDiffView.tsx:88-102). `before` is the diffBase
 * (older) workspace; `after` is the primary. `beforeProc` is null when the
 * process is newly added — it diffs against the empty scaffold.
 */
export function renderProcessBpmnDiffSvg(
  before: ResolvedModel,
  after: ResolvedModel,
  beforeProc: ProcessModel | null,
  afterProc: ProcessModel,
): string {
  const b = beforeProc ? freshBpmnScaffold(before, beforeProc) : EMPTY_SCAFFOLD;
  const a = freshBpmnScaffold(after, afterProc);
  const keys: DiffKeyContext = {
    beforeModules: moduleNameIndex(before.modules),
    afterModules: moduleNameIndex(after.modules),
    beforeCallables: callableIndex(before),
    afterCallables: callableIndex(after),
  };
  const union = diffScaffolds(b, a, keys);
  return renderProcessSvg(bpmnLayout(union), BPMN_EXPORT_OPTIONS);
}
